import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BoltIcon, PhotoIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import './InferencesModal.css';
import { API_BASE_URL } from '../config';
import { useModal } from '../services/ModalService';
import { MediaModal } from './activities/Media';
import { sanitizeBodyEstimates } from './activities/bodyEstimateEligibility';

const SESSION_PAGE_SIZE = 8;

const STATUS_LABEL = {
  completed: 'Done',
  processing: 'Processing',
  failed: 'Failed',
  skipped: 'Skipped',
  pending: 'Pending',
  unknown: 'Unknown',
};

// How long a thumbnail must stay inside the viewport before we actually
// start downloading it. When the user is flinging through the list at speed,
// each thumb is on screen for only a few ms — waiting this long means we
// skip fetching anything they're scrolling past and instead reserve all
// bandwidth for thumbs they actually stop on.
const THUMB_LOAD_DEBOUNCE_MS = 120;

// Component-level lazy thumbnail: owns its own IntersectionObserver and
// AbortController so a thumb that scrolls out of view before its fetch
// completes is cancelled, freeing the connection for visible thumbs.
const LazyThumb = ({
  file,
  thumbUrl,
  status,
  matched,
  statusLabel,
  onLoad,
  onClick,
}) => {
  const ref = useRef(null);
  const controllerRef = useRef(null);
  const timerRef = useRef(null);
  const hasUrlRef = useRef(!!thumbUrl);

  useEffect(() => {
    hasUrlRef.current = !!thumbUrl;
  }, [thumbUrl]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const cancelPending = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (hasUrlRef.current || controllerRef.current || timerRef.current) continue;
            timerRef.current = setTimeout(() => {
              timerRef.current = null;
              if (hasUrlRef.current) return;
              const controller = new AbortController();
              controllerRef.current = controller;
              Promise.resolve(onLoad(file, controller.signal))
                .finally(() => {
                  if (controllerRef.current === controller) {
                    controllerRef.current = null;
                  }
                });
            }, THUMB_LOAD_DEBOUNCE_MS);
          } else {
            // Out of view: abort if still in flight. Already-loaded thumbs
            // stay loaded (no refetch when scrolled back).
            if (!hasUrlRef.current) cancelPending();
          }
        }
      },
      { rootMargin: '200px 0px' }
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      cancelPending();
    };
  }, [file, onLoad]);

  return (
    <div
      ref={ref}
      className="inf-thumb"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {thumbUrl ? (
        <img
          className="inf-thumb-img"
          src={thumbUrl}
          alt={matched || 'media'}
          loading="lazy"
        />
      ) : (
        <div className="inf-thumb-skeleton" />
      )}
      <div className={`inf-thumb-status status-${status}`}>{statusLabel}</div>
      {matched && <div className="inf-thumb-tag" title={matched}>{matched}</div>}
    </div>
  );
};

const InferencesModal = ({ backendService }) => {
  const modalService = useModal();
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [filesError, setFilesError] = useState(null);
  const [job, setJob] = useState(null);
  const [jobError, setJobError] = useState(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [thumbUrls, setThumbUrls] = useState({});
  // Lazy-render sessions: large libraries (hundreds of photos / dozens of
  // sessions) tanked the modal because every <img> + status pill rendered up
  // front and every 256px thumbnail was fetched in parallel. Render in pages
  // of SESSION_PAGE_SIZE and bump via an IntersectionObserver sentinel.
  const [displayCount, setDisplayCount] = useState(SESSION_PAGE_SIZE);
  const sentinelRef = useRef(null);

  const currentUserId = useMemo(() => {
    const user = backendService.getUser ? backendService.getUser() : null;
    if (!user) return null;
    return user._id || user.user_id || user.id || null;
  }, [backendService]);

  const jobInProgress = job && ['pending', 'processing', 'cancelling'].includes(job.status);
  const jobCancelling = job?.status === 'cancelling';
  const jobCancelled = job?.status === 'cancelled';
  const progress = job?.progress || { current: 0, total: 0, percentage: 0, message: '' };

  const loadFiles = useCallback(async () => {
    setFilesLoading(true);
    setFilesError(null);
    try {
      const data = await backendService.fetchInferences();
      // Defend against legacy/retained unsanitized overview responses too.
      const safeFiles = (Array.isArray(data?.files) ? data.files : []).map(file => file.latestInference ? {
        ...file,
        latestInference: { ...file.latestInference,
          result: sanitizeBodyEstimates(file.latestInference.result, file.fileId) },
      } : file);
      setFiles(safeFiles);
    } catch (error) {
      console.error('Failed to load inference list', error);
      setFilesError('Unable to load inference data.');
    } finally {
      setFilesLoading(false);
    }
  }, [backendService]);

  const loadJobStatus = useCallback(async () => {
    try {
      const status = await backendService.getInferenceJobStatus();
      setJob(status);
      setJobError(null);
    } catch (error) {
      console.error('Failed to load inference job status', error);
      setJobError('Unable to fetch inference job status.');
    }
  }, [backendService]);

  useEffect(() => {
    loadFiles();
    loadJobStatus();
  }, [loadFiles, loadJobStatus]);

  // Thumbnail loading moved further down to depend on the *visible*
  // session window (not the full file list), so a user with hundreds of
  // historical photos doesn't kick off hundreds of /media/:id/url fetches
  // before they've even scrolled.

  useEffect(() => {
    let wsUrl;
    if (API_BASE_URL) {
      const apiUrl = new URL(API_BASE_URL || window.location.origin);
      const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${apiUrl.host}/ws`;
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/ws`;
    }

    const ws = new WebSocket(wsUrl);
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'inference_job_progress') {
          if (currentUserId && message.userId && message.userId !== currentUserId) {
            return;
          }
          setJob(message.job);
          if (message.job.status === 'completed') {
            setStatusMessage('Inference rerun completed successfully.');
            loadFiles();
          } else if (message.job.status === 'failed') {
            const failure = message.job.error || 'The inference rerun failed.';
            setStatusMessage(failure);
          } else if (message.job.status === 'cancelled') {
            setStatusMessage('Inference rerun cancelled.');
            loadFiles();
          } else if (message.job.status === 'cancelling') {
            setStatusMessage('Cancelling inference rerun...');
          }
        } else if (message.type === 'inference_activity_updated') {
          if (currentUserId && message.userId && message.userId !== currentUserId) {
            return;
          }
          const updates = Array.isArray(message.files) ? message.files : [];
          if (updates.length === 0) return;
          const byFileId = new Map(updates.map((u) => [String(u.fileId), u]));
          setFiles((prev) => prev.map((f) => {
            const u = byFileId.get(String(f.fileId));
            if (!u) return f;
            return {
              ...f,
              inferenceStatus: u.inferenceStatus ?? f.inferenceStatus,
              latestInference: u.latestInference !== undefined ? u.latestInference : f.latestInference,
            };
          }));
        }
      } catch (err) {
        console.error('Failed to parse websocket payload', err);
      }
    };

    return () => {
      ws.close();
    };
  }, [currentUserId, loadFiles]);

  // Group files by activity so the UI mirrors how the backend now batches
  // inferences (one model call per activity covering all of that session's
  // photos jointly).
  const sessions = useMemo(() => {
    const map = new Map();
    for (const f of files) {
      const key = String(f.activityId);
      if (!map.has(key)) {
        map.set(key, {
          activityId: key,
          activityName: f.activityName || 'Media',
          activityCreatedAt: f.activityCreatedAt,
          files: [],
        });
      }
      map.get(key).files.push(f);
    }
    return Array.from(map.values()).sort((a, b) => {
      const aDate = Math.max(...a.files.map((x) => new Date(x.fileCreatedAt || x.activityCreatedAt || 0).getTime()));
      const bDate = Math.max(...b.files.map((x) => new Date(x.fileCreatedAt || x.activityCreatedAt || 0).getTime()));
      return bDate - aDate;
    });
  }, [files]);

  const totalImages = files.length;
  const totalSessions = sessions.length;

  const visibleSessions = useMemo(
    () => sessions.slice(0, displayCount),
    [sessions, displayCount]
  );
  const hasMoreSessions = displayCount < sessions.length;

  // Reset pagination whenever the underlying session list shrinks (e.g.,
  // after a reload) so we don't accidentally over-page into nothing. We
  // intentionally don't reset on growth so the user keeps the scroll
  // position they've already revealed.
  useEffect(() => {
    if (displayCount > Math.max(SESSION_PAGE_SIZE, sessions.length)) {
      setDisplayCount(Math.max(SESSION_PAGE_SIZE, sessions.length));
    }
  }, [sessions.length, displayCount]);

  // Bump displayCount when the bottom sentinel scrolls into view.
  useEffect(() => {
    if (!hasMoreSessions) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setDisplayCount((c) => c + SESSION_PAGE_SIZE);
        }
      },
      { rootMargin: '300px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMoreSessions, visibleSessions.length]);

  // Lazy-load 256px auth'd thumbnail blob URLs ONE-BY-ONE as each <LazyThumb>
  // scrolls into view. Fast-scrolling past a thumb cancels its in-flight
  // request so bandwidth isn't wasted on images the user already passed —
  // every byte goes toward the thumbnails currently on screen.
  const thumbUrlsRef = useRef({});
  useEffect(() => { thumbUrlsRef.current = thumbUrls; }, [thumbUrls]);

  // Track every blob URL we mint so we can revoke them all when the modal
  // unmounts. Revoking eagerly (e.g., on visible-window change) would break
  // thumbs the user scrolls back to.
  const createdBlobUrlsRef = useRef([]);
  useEffect(() => {
    return () => {
      for (const u of createdBlobUrlsRef.current) {
        try { URL.revokeObjectURL(u); } catch (e) { /* noop */ }
      }
      createdBlobUrlsRef.current = [];
    };
  }, []);

  const loadThumb = useCallback(async (file, signal) => {
    if (thumbUrlsRef.current[file.fileId]) return;
    try {
      const url = await backendService.fetchMediaFileUrl(
        file.activityId,
        file.fileId,
        '256px',
        { signal }
      );
      if (signal?.aborted) {
        try { URL.revokeObjectURL(url); } catch (e) { /* noop */ }
        return;
      }
      createdBlobUrlsRef.current.push(url);
      setThumbUrls((prev) => {
        if (prev[file.fileId]) {
          try { URL.revokeObjectURL(url); } catch (e) { /* noop */ }
          return prev;
        }
        return { ...prev, [file.fileId]: url };
      });
    } catch (err) {
      // AbortError / CanceledError are expected when scrolling past — swallow.
    }
  }, [backendService]);

  const handleRerunAll = async () => {
    if (jobInProgress || isTriggering) return;
    setIsTriggering(true);
    setStatusMessage(null);
    try {
      await backendService.runInferenceRerun();
      await loadJobStatus();
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to start inference rerun.';
      setStatusMessage(message);
    } finally {
      setIsTriggering(false);
    }
  };

  const handleCancel = async () => {
    if (!jobInProgress) return;
    setStatusMessage(null);
    try {
      await backendService.cancelInferenceRerun();
      await loadJobStatus();
      setStatusMessage('Cancellation requested. Finishing the current session...');
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to cancel inference rerun.';
      setStatusMessage(message);
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString();
  };

  const formatBodyFat = (inference) => {
    const best = inference?.result?.estimated_body_fat_range?.best_estimate;
    if (typeof best === 'number' && Number.isFinite(best)) {
      return `${(best * 100).toFixed(1)}%`;
    }
    return null;
  };

  const sessionBodyFat = (session) => {
    for (const f of session.files) {
      const v = formatBodyFat(f.latestInference);
      if (v) return v;
    }
    return '—';
  };

  const sessionConfidence = (session) => {
    for (const f of session.files) {
      const c = f.latestInference?.result?.confidence;
      if (c) return c;
    }
    return null;
  };

  const sessionStatusCounts = (session) => {
    const counts = {};
    for (const f of session.files) {
      const key = (f.inferenceStatus || 'unknown').toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  };

  // Open the same lightbox/MediaModal used by the activity feed when the
  // user taps an inference thumbnail. We rebuild the items in the shape
  // MediaModal expects ({ file: { _id, type, name }, activityId, ... })
  // from the inferences payload and let MediaModal lazily fetch full-size
  // URLs on its own. We deliberately do NOT forward the 256px thumbUrls
  // because MediaModal skips fetching the original whenever a URL is
  // already cached for the file id.
  const openLightbox = useCallback((session, fileIndex) => {
    const initialMediaItems = (session.files || []).map((f) => ({
      file: {
        _id: f.fileId,
        type: f.fileType || 'image/jpeg',
        name: f.fileName || 'photo',
        inferenceStatus: f.inferenceStatus,
        inferences: f.latestInference ? [f.latestInference] : [],
      },
      activityId: session.activityId,
      activityName: session.activityName,
      activityDate: session.activityCreatedAt || f.fileCreatedAt,
    }));
    modalService.open(MediaModal, {
      initialMediaItems,
      startFileId: session.files[fileIndex]?.fileId,
      backendService,
      // Minimal stand-in for the activity-feed `media` prop (only _id /
      // created_at are read, and only for the activity that owns the
      // currently-displayed file). Passing {} keeps the delete-callback
      // path from crashing while harmlessly skipping the in-place
      // setFiles() update we don't have access to here.
      media: {},
    });
  }, [modalService, backendService]);

  const renderSessions = () => {
    if (filesLoading) {
      return <div className="inf-empty">Loading sessions…</div>;
    }
    if (filesError) {
      return <div className="inf-empty inf-error-text">{filesError}</div>;
    }
    if (!sessions.length) {
      return <div className="inf-empty">No media sessions yet. Upload photos from a check-in to get started.</div>;
    }

    return (
      <div className="inf-session-list">
        {visibleSessions.map((session) => {
          const counts = sessionStatusCounts(session);
          const bf = sessionBodyFat(session);
          const conf = sessionConfidence(session);
          return (
            <div key={session.activityId} className="inf-session-card">
              <div className="inf-session-head">
                <div className="inf-session-title">
                  <span className="inf-session-name">{session.activityName}</span>
                  <span className="inf-session-meta">
                    {formatDate(session.activityCreatedAt || session.files[0]?.fileCreatedAt)}
                    <span className="inf-dot">•</span>
                    {session.files.length} photo{session.files.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="inf-session-est">
                  <span className="inf-bf-value">{bf}</span>
                  {conf && <span className="inf-bf-conf">{conf} confidence</span>}
                </div>
              </div>

              <div className="inf-thumbs">
                {session.files.map((f, fileIdx) => {
                  const status = (f.inferenceStatus || 'unknown').toLowerCase();
                  const matched = f.latestInference?.result?.matched_requirement;
                  return (
                    <LazyThumb
                      key={f.fileId}
                      file={f}
                      thumbUrl={thumbUrls[f.fileId]}
                      status={status}
                      matched={matched}
                      statusLabel={STATUS_LABEL[status] || status}
                      onLoad={loadThumb}
                      onClick={() => openLightbox(session, fileIdx)}
                    />
                  );
                })}
              </div>

              <div className="inf-session-statusrow">
                {Object.entries(counts).map(([k, v]) => (
                  <span key={k} className={`inf-pill status-${k}`}>{v} {STATUS_LABEL[k] || k}</span>
                ))}
              </div>
            </div>
          );
        })}
        {hasMoreSessions && (
          <div ref={sentinelRef} className="inf-sentinel">
            <div className="inf-sentinel-spinner" />
            <span>Loading more sessions…</span>
          </div>
        )}
      </div>
    );
  };

  const messageIsError = !!(
    (statusMessage && /fail|error|cancel/i.test(statusMessage)) || jobError
  );

  return (
    <div className="inferences-modal">
      <div className="inf-header">
        <h2>Body composition analysis</h2>
        <p className="inf-subhead">
          One AI pass per session. Each check-in's photos (front, side, back…) are analyzed
          together so estimates and matched requirements stay consistent across angles.
        </p>
      </div>

      <div className="inf-action-card">
        <div className="inf-action-copy">
          <h4>
            <ArrowPathIcon className="inf-h4-icon" />
            Re-run on every session
          </h4>
          <p>
            Re-runs inference activity-by-activity, batching each session's photos into a
            single multi-image call. Useful after prompt or model upgrades — your historical
            data gets the same treatment as new uploads.
          </p>
          <div className="inf-action-stats">
            <span><strong>{totalSessions}</strong> session{totalSessions === 1 ? '' : 's'}</span>
            <span className="inf-dot">•</span>
            <span><strong>{totalImages}</strong> image{totalImages === 1 ? '' : 's'} total</span>
          </div>
        </div>
        <div className="inf-action-buttons">
          <button
            className="btn-primary inf-action-btn"
            onClick={handleRerunAll}
            disabled={jobInProgress || isTriggering || totalImages === 0}
          >
            <BoltIcon className="inf-btn-icon" />
            {jobInProgress ? 'Run in progress…' : 'Re-run all sessions'}
          </button>
          {jobInProgress && (
            <button
              className="btn-cancel inf-action-btn"
              onClick={handleCancel}
              disabled={jobCancelling}
            >
              {jobCancelling ? 'Cancelling…' : 'Cancel'}
            </button>
          )}
        </div>
      </div>

      {(jobInProgress || ['failed', 'completed', 'cancelled'].includes(job?.status)) && (
        <div className="inf-job-card">
          <div className="inf-job-head">
            <span className={`inf-pill status-${job?.status || 'unknown'}`}>
              {job?.status === 'processing' && <ArrowPathIcon className="inf-pill-icon spin" />}
              {job?.status === 'completed' && <CheckCircleIcon className="inf-pill-icon" />}
              {job?.status === 'failed' && <ExclamationTriangleIcon className="inf-pill-icon" />}
              {job?.status || 'idle'}
            </span>
            {job?.result && (
              <span className="inf-job-summary">
                {job.result.processed || 0} processed
                {typeof job.result.failed === 'number' && job.result.failed > 0 &&
                  ` • ${job.result.failed} failed`}
              </span>
            )}
          </div>
          {(jobInProgress || progress.percentage > 0 || jobCancelled) && (
            <div className="inf-progress">
              <div className="inf-progress-bar">
                <div className="inf-progress-fill" style={{ width: `${progress.percentage || 0}%` }}></div>
              </div>
              <div className="inf-progress-text">
                {progress.percentage || 0}% — {progress.message || 'Preparing…'}
              </div>
            </div>
          )}
        </div>
      )}

      {(statusMessage || jobError) && (
        <div className={`inf-toast ${messageIsError ? 'inf-toast-error' : 'inf-toast-success'}`}>
          {statusMessage || jobError}
        </div>
      )}

      <div className="inf-section-head">
        <h3>
          <PhotoIcon className="inf-h3-icon" />
          Sessions
        </h3>
        <span className="inf-section-count">{totalSessions}</span>
      </div>

      {renderSessions()}
    </div>
  );
};

export default InferencesModal;
